// Tauri integration for Ava webapp
// This file provides compatibility layer between webapp and Tauri desktop app
// Requires Tauri 2.8.0+ (stable API)

import { isMac } from '../utils';

// Dynamic imports to avoid loading Tauri packages in non-Tauri environments
let invoke: any;
let emit: any;
let listen: any;
let getCurrentWindow: any;
let PhysicalSize: any;
let writeText: any;
let save: any;
let writeFile: any;
let open: any;

// Initialize Tauri imports only when needed
const initTauriImports = async () => {
  if (!window.__TAURI__) return;

  const coreModule = await import('@tauri-apps/api/core');
  invoke = coreModule.invoke;

  const eventModule = await import('@tauri-apps/api/event');
  emit = eventModule.emit;
  listen = eventModule.listen;

  const windowModule = await import('@tauri-apps/api/window');
  getCurrentWindow = windowModule.getCurrentWindow;
  PhysicalSize = windowModule.PhysicalSize;

  const clipboardModule = await import('@tauri-apps/plugin-clipboard-manager');
  writeText = clipboardModule.writeText;

  const dialogModule = await import('@tauri-apps/plugin-dialog');
  save = dialogModule.save;

  const fsModule = await import('@tauri-apps/plugin-fs');
  writeFile = fsModule.writeFile;

  const shellModule = await import('@tauri-apps/plugin-shell');
  open = shellModule.open;
};

// Initialize imports on module load if Tauri is available
if (window.__TAURI__) {
  initTauriImports().catch((err) => console.error('Failed to load Tauri modules:', err));
}

// Used in order to initiate dragging of the window.
let mouseDown = false;
let requestedDragging = false;

export const toggleDragging = (value: boolean) => {
  if (value) {
    document.addEventListener('mousedown', mouseDownListenerForDrag);
    document.addEventListener('mouseup', mouseUpListenerForDrag);
    document.addEventListener('mousemove', mouseMoveListenerForDrag);
  } else {
    document.removeEventListener('mousedown', mouseDownListenerForDrag);
    document.removeEventListener('mouseup', mouseUpListenerForDrag);
    document.removeEventListener('mousemove', mouseMoveListenerForDrag);
  }
};

export const setWindowSize = async (height: number, width: number) => {
  // Use logical size for consistency across DPIs/Monitors
  const window = getCurrentWindow();
  const scaleFactor = await window.scaleFactor();
  const physicalWidth = width * scaleFactor;
  const physicalHeight = height * scaleFactor;
  const size = new PhysicalSize(physicalWidth, physicalHeight);
  await window.setSize(size);
};

export const getWindowSize = async () => {
  // we want to work with logical size for consistency across DPIs/Monitors
  const currentWindow = getCurrentWindow();
  const scaleFactor = await currentWindow.scaleFactor();
  const physicalSize = await currentWindow.innerSize();
  const logicalWidth = physicalSize.width / scaleFactor;
  const logicalHeight = physicalSize.height / scaleFactor;
  return { width: logicalWidth, height: logicalHeight };
};

export const setupDesktopIntegration = () => {
  if (window.isElectron) return;
  if (!window.__TAURI__) return;

  // Set up stub objects immediately to prevent undefined errors
  window.isElectron = {};
  window.desktopIntegration = {
    openExternalURL: async (url: string) => {},
    resizeWindowToDefault: () => {},
    writeToClipboard: async (text: string) => {},
  };
  window.electronCurrentWindow = {
    minimize: () => {},
    maximize: () => {},
    unmaximize: () => {},
    close: () => {},
    setIgnoreMouseEvents: (...args) => {},
  };
  window.electronApp = {
    getVersion: () => window.isElectron.version,
    getVersionAsync: () => Promise.resolve(''),
  };
  window.electronIPC = {
    sendCCMode: (ccMode, value = true) => {},
    sendSetInCaptionMode: (value: boolean) => {},
    sendChangeVolume: (newVolume) => Promise.resolve(),
    invokeGetVolume: async () => 0,
    // Event listeners that return promises (for useEffectWithUnsubscribe compatibility)
    onEndConversation: (callback) => Promise.resolve(() => {}),
    onStartConversation: (callback) => Promise.resolve(() => {}),
    onSignOut: (callback) => Promise.resolve(() => {}),
    onAudioAllStopped: (callback) => Promise.resolve(() => {}),
    onAudioOutputChanged: (callback) => Promise.resolve(() => {}),
    onTauriWebRTCConnectionStateChange: (callback) => Promise.resolve(() => {}),
    onLogEventReceived: (callback) => Promise.resolve(() => {}),
    onVolumeReceived: (callback) => Promise.resolve(() => {}),
    onScreenCaptureError: (callback) => Promise.resolve(() => {}),
    emitDebug: (eventName, payload) => {},
    // Stubs for Electron compatibility - not implemented in Tauri
    sendGoInFloatingMode: () => {},
    onDesktopAppLoaded: (callback) => {},
    onLog: (callback) => {},
    onTrack: (callback) => {},
    onSetElectronCaptionMode: (callback) => {},
    onWindowActivated: (callback) => {},
    onceMacInternalAudioInstallation: (callback) => {},
    onceMacMicrophoneAccessResult: (callback) => {},
    sendDoneLoading: () => {},
    sendSetIsInConversation: (arg) => {},
    sendSetIsLoggedIn: (arg) => {},
    onCreateNewConversation: (callback) => {},
    onSignOutAndShowSignIn: (callback) => {},
    sendAvaWebAvailable: () => {},
    getAppUrl: () => {},
    sendActivateMacInternalAudio: () => {},
    sendDeactivateMacInternalAudio: () => {},
    onMultiOutputChanged: (callback) => {},
    sendSetupAudioLoaded: () => {},
    sendAskMacMicrophoneAccess: () => {},
    sendSetupMacInternalAudio: () => {},
    sendSyncCheckAudioSetup_UNSAFE: () => {},
    invokeCheckAudioSetup: async () => {},
    invokeCheckLaunchedOnLogin: async () => {},
    invokeHasSeenInternalAudioMicTour: async () => {},
  };

  // Initialize Tauri imports and replace stubs with real implementations
  initTauriImports()
    .then(() => {
      setupTauriIntegrations();
    })
    .catch((err) => {
      console.error('Failed to initialize Tauri imports:', err);
    });
};

const setupTauriIntegrations = () => {
  window.desktopIntegration = {
    openExternalURL: async (url: string) => {
      await open(url);
    },
    resizeWindowToDefault: () => {},
    writeToClipboard: async (text: string) => {
      await writeText(text);
    },
  };

  window.isElectron = {};

  tauriInvoke('plugin:os_specific|get_os_name').then((os_name) => {
    window.isElectron.electronOS = os_name; // Windows, macOS, Linux
  });

  tauriInvoke('get_version').then((version) => {
    window.isElectron.version = version;
  });

  window.electronCurrentWindow = {
    minimize: () => {
      getCurrentWindow().minimize();
    },
    maximize: () => {
      getCurrentWindow().maximize();
    },
    unmaximize: () => {
      getCurrentWindow().unmaximize();
    },
    close: () => {
      // On Mac we do not close the main window - we just hide it instead.
      tauriInvoke('plugin:positioner|close_main_window');
    },
    // This is used only on Mac, in order to make the transparent window click-through
    setIgnoreMouseEvents: (...args) => {},
  };

  window.electronApp = {
    // Might sometimes return undefined before the promise finishes,
    // but we are not making it async to stay consistent with electron.
    getVersion: () => window.isElectron.version,
    getVersionAsync: () => tauriInvoke('get_version'),
  };

  window.electronIPC = {
    sendCCMode: (ccMode, value = true) => {
      tauriInvoke('plugin:positioner|position_cc', { ccMode, value });
    },

    sendSetInCaptionMode: (value: boolean) => {
      mouseDown = false;
      toggleDragging(value);
      tauriInvoke('plugin:positioner|position_cc', { ccMode: value ? 'begin_cc' : 'expand', value: false });
    },

    sendChangeVolume: (newVolume) => {
      return tauriInvoke('plugin:os_specific|set_volume', { volume: newVolume / 100.0 });
    },

    invokeGetVolume: async () => {
      const result = ((await tauriInvoke('plugin:os_specific|get_volume')) as number) * 100;
      return result;
    },

    // Event listeners
    onEndConversation: listenToTauriEvent('request_end_conversation'),
    onStartConversation: listenToTauriEvent('request_start_conversation'),
    onSignOut: listenToTauriEvent('request_sign_out'),
    onAudioAllStopped: listenToTauriEvent('audio-all-stopped'),
    onAudioOutputChanged: listenToTauriEvent('audio-output-changed'),
    onTauriWebRTCConnectionStateChange: listenToTauriEvent('webrtc-connection-state-change'),
    onLogEventReceived: listenToTauriEvent('log_event'),
    onVolumeReceived: listenToTauriEvent('volume'),
    onScreenCaptureError: listenToTauriEvent('screen-capture-error'),

    emitDebug: (eventName, payload) => {
      tauriEventEmit(eventName, payload);
    },

    // Stubs for Electron compatibility - not implemented in Tauri
    sendGoInFloatingMode: () => {},
    onDesktopAppLoaded: (callback) => {},
    onLog: (callback) => {},
    onTrack: (callback) => {},
    onSetElectronCaptionMode: (callback) => {},
    onWindowActivated: (callback) => {},
    onceMacInternalAudioInstallation: (callback) => {},
    onceMacMicrophoneAccessResult: (callback) => {},
    sendDoneLoading: () => {},
    sendSetIsInConversation: (arg) => {},
    sendSetIsLoggedIn: (arg) => {},
    onCreateNewConversation: (callback) => {},
    onSignOutAndShowSignIn: (callback) => {},
    sendAvaWebAvailable: () => {},
    getAppUrl: () => {},
    sendActivateMacInternalAudio: () => {},
    sendDeactivateMacInternalAudio: () => {},
    onMultiOutputChanged: (callback) => {},
    sendSetupAudioLoaded: () => {},
    sendAskMacMicrophoneAccess: () => {},
    sendSetupMacInternalAudio: () => {},
    sendSyncCheckAudioSetup_UNSAFE: () => {},
    invokeCheckAudioSetup: async () => {},
    invokeCheckLaunchedOnLogin: async () => {},
    invokeHasSeenInternalAudioMicTour: async () => {},
  };
};

export const mouseDownListenerForDrag = async (e) => {
  const noDragSelector = '.tauri-no-drag';
  if (e.target?.closest(noDragSelector)) return;
  const currentHeight = document.documentElement.clientHeight;
  const currentWidth = document.documentElement.clientWidth;
  if (e.clientX < 5 || e.clientX > currentWidth - 5 || e.clientY < 5 || e.clientY > currentHeight - 5) return;
  mouseDown = true;
  requestedDragging = false;
};

export const mouseUpListenerForDrag = async () => {
  requestedDragging = false;
  mouseDown = false;
  document.removeEventListener('click', stubClickListener);
};

export const mouseMoveListenerForDrag = async () => {
  if (!mouseDown || requestedDragging) return;
  mouseDown = false;
  requestedDragging = true;
  if (isMac) {
    document.addEventListener('click', stubClickListener, { capture: true, once: true });
  }
  await getCurrentWindow().startDragging();
};

const stubClickListener = async (e) => {
  e.stopPropagation();
  e.preventDefault();
};

const listenToTauriEvent = (eventName: string) => (callback) => {
  if (!window.__TAURI__) return Promise.resolve(() => {});
  if (!listen) {
    console.error(`Tauri listen not initialized for event ${eventName}`);
    return Promise.resolve(() => {});
  }
  return listen(eventName, (event) => {
    if (!event || event.payload === undefined) {
      console.warn(`Received event ${eventName} with no payload`);
      return;
    }
    if (eventName === 'log_event') {
      console.log(`Tauri_Log:${event.payload}`);
      return;
    }
    if (eventName !== 'volume') {
      console.log(`tauri event: ${eventName} with payload ${JSON.stringify(event.payload)}`);
    }
    callback(event.payload);
  }).catch((error) => {
    console.error(`Failed to listen to event ${eventName}:`, error);
    return () => {}; // Return a no-op unlisten function on error
  });
};

// Export for external use
export const tauriEventListen = (eventName: string, callback: (event: any) => void) => {
  if (!window.__TAURI__) return Promise.resolve(() => {});
  if (!listen) {
    console.error(`Tauri listen not initialized for event ${eventName}`);
    return Promise.resolve(() => {});
  }
  return listen(eventName, (event) => {
    if (!event) {
      console.warn(`Received event ${eventName} with no event object`);
      return;
    }
    // Pass the full event object, not just the payload
    callback(event);
  }).catch((error) => {
    console.error(`Failed to listen to event ${eventName}:`, error);
    return () => {};
  });
};

// Export for external use
export const tauriWindowGetCurrent = () => {
  if (!window.__TAURI__) throw new Error('getCurrentWindow invoked in non-tauri context');
  return getCurrentWindow();
};

export const tauriEventEmit = async (eventName, payload) => {
  if (!window.__TAURI__) return;
  return emit(eventName, payload);
};

export const tauriInvoke = async (cmd, args?, options?) => {
  if (!window.__TAURI__) throw new Error('invoke invoked in non-tauri context');
  try {
    return await invoke(cmd, args, options);
  } catch (e) {
    // On Rust side we refactored plugins into non-plugin modules
    if (cmd === 'plugin:redux_state|update') {
      return invoke('redux_state_update', args, options);
    } else if (cmd.startsWith('plugin:')) {
      cmd = cmd.replace(/plugin:.*\|/, '');
      return invoke(cmd, args, options);
    } else {
      throw e;
    }
  }
};

export const tauriSaveAs = async (blob: Blob, filename: string) => {
  const filePath = await save({
    defaultPath: filename,
  });
  if (!filePath) return;
  try {
    await writeFile(filePath, new Uint8Array(await blob.arrayBuffer()));
  } catch (e) {
    console.error('failed to save using writeFile: ', e);
  }
};

export const checkIfEligibleForCCV2 = (): boolean => {
  // min version is '2.1.9'
  if (!window.__TAURI__) return false;
  const version = window.electronApp.getVersion();
  if (!version) return false;
  const parts = version.split('.');
  if (parts[2]?.includes('-')) {
    parts[2] = parts[2].split('-')[0];
  }
  const numberParts = parts.map((part) => parseInt(part));
  if (numberParts[0] > 2) return true;
  else if (numberParts[0] === 2 && numberParts[1] > 1) return true;
  else if (numberParts[0] === 2 && numberParts[1] === 1 && numberParts[2] >= 9) return true;
  else return false;
};
